import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Badge, StatusBadge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { type Column, DataTable } from '../../components/ui/DataTable.js';
import { Input, Select } from '../../components/ui/FormField.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { useAbility } from '../../context/AbilityContext.js';
import { useListControls } from '../../hooks/useListControls.js';
import { useRoleOptions } from '../../hooks/useRoleOptions.js';
import { api } from '../../services/api.js';
import { RESOURCE_KEYS } from '../../services/resourceKeys.js';
import { BulkActionBar } from './BulkActionBar.js';
import { type BanInfo, banShort } from './banReason.js';
import { CUSTOMER, RoleChips, breaksRule } from './roleCard.js';

interface User {
  _id: string;
  first_name: string;
  last_name: string;
  email: string;
  status: string;
  email_verified_at?: string;
  identity_verification?: { status: string };
  created_at: string;
  roles: string[];
  /** 376: organisation roles held through live supplier memberships (absent from an older backend). */
  membership_roles?: string[];
  /** 381 (#1271): why the account is banned (null when it is not). */
  ban?: Pick<BanInfo, 'reason' | 'booking_ref'> | null;
  /** 330: the customer's campus, with its name (including a deactivated one). */
  campus?: { _id: string; name: string; is_active: boolean } | null;
}

interface UsersResponse {
  items: User[];
  total: number;
  page: number;
  pages: number;
}

/**
 * 181: how each identity state reads in a list. The words are the customer-facing ones the app
 * already uses (`app/src/pages/account/identity/status.ts`), so staff and customer describe the
 * same state the same way.
 */
type IdentityStatus = 'pending' | 'submitted' | 'approved' | 'rejected' | 'resubmitted';

const IDENTITY_VIEW: Record<
  IdentityStatus,
  { label: string; variant: 'success' | 'warning' | 'danger' | 'default' }
> = {
  approved: { label: 'Verified', variant: 'success' },
  submitted: { label: 'In review', variant: 'warning' },
  rejected: { label: 'Rejected', variant: 'danger' },
  pending: { label: 'Not submitted', variant: 'default' },
  // 199 (#288): a customer replaced a document on an already-approved record. Deliberately NOT
  // "In review" — that reads as a first-time submission, and staff need to know this one is a
  // re-review of a decision they already made once (research.md R5).
  resubmitted: { label: 'Re-review needed', variant: 'danger' },
};

function identityStatusView(status: string | undefined) {
  return IDENTITY_VIEW[(status ?? 'pending') as IdentityStatus] ?? IDENTITY_VIEW.pending;
}

const columns: Column<User>[] = [
  {
    key: '_id',
    header: 'Name',
    render: (row) => (
      <Link to={`/users/${row._id}`} className="text-primary hover:underline font-medium">
        {row.first_name} {row.last_name}
      </Link>
    ),
  },
  { key: 'email', header: 'Email' },
  // #20: the three facts below are independent fields, and the reporter reads
  // this list expecting each column to name one of them. `status` here is email
  // verification; the account lifecycle keeps its own "Account" column.
  {
    key: 'email_verified_at',
    header: 'Status',
    render: (row) => <StatusBadge status={row.email_verified_at ? 'active' : 'inactive'} />,
  },
  {
    key: 'roles',
    // 376 (#1262, design F): Customer plus at most one staff role, as chips; an account from before the clean-up
    // that breaks the rule is flagged so staff can fix it.
    header: 'Roles',
    render: (row) => {
      // The account's roles and those held through a membership, once each: a membership role counts too.
      const held = [...new Set([...row.roles, ...(row.membership_roles ?? [])])].sort((a, b) =>
        a === CUSTOMER ? -1 : b === CUSTOMER ? 1 : 0,
      );
      return held.length ? (
        <span className="inline-flex flex-wrap items-center gap-1.5">
          <RoleChips names={held} />
          {breaksRule(held) && (
            <span data-testid="role-flag">
              <Badge variant="warning">two staff roles</Badge>
            </span>
          )}
        </span>
      ) : (
        <span className="text-muted">—</span>
      );
    },
  },
  {
    key: 'identity_verification',
    header: 'Identity',
    /**
     * 🔴 181 (FR-017): four states, not Yes/No.
     *
     * This column used to render `status === 'approved' ? 'Yes' : 'No'`, so NOT-SUBMITTED,
     * IN-REVIEW and REJECTED were all "No" — a customer waiting for someone to look at their
     * documents was indistinguishable from one who had never uploaded anything. Adding the queue
     * filter without fixing this would have let staff filter to a list whose rows they still could
     * not tell apart.
     */
    render: (row) => {
      const view = identityStatusView(row.identity_verification?.status);
      return <Badge variant={view.variant}>{view.label}</Badge>;
    },
  },
  // Mutated by the bulk status action on this very page and by Disable/Enable on
  // the detail page, so it stays visible in the list that performs it.
  {
    key: 'status',
    header: 'Account',
    render: (row) => <StatusBadge status={row.status} />,
  },
  // 381 (#1271): why a banned account is banned.
  {
    key: 'ban',
    header: 'Ban reason',
    render: (row) => {
      const short = row.status === 'banned' ? banShort(row.ban) : null;
      return short ? (
        <span data-testid="user-row-ban">{short}</span>
      ) : (
        <span className="text-muted">—</span>
      );
    },
  },
  // 330 (FR-011): the campus the customer sees and books at.
  {
    key: 'campus',
    header: 'Campus',
    render: (row) => (
      <span data-testid="user-row-campus">
        {row.campus
          ? `${row.campus.name}${row.campus.is_active ? '' : ' (inactive)'}`
          : 'Not chosen'}
      </span>
    ),
  },
  {
    key: 'created_at',
    header: 'Joined',
    render: (row) =>
      new Date(row.created_at).toLocaleDateString('en-MY', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }),
  },
];

/** The identity filter's values: `awaiting` (355) is submitted and resubmitted together. */
const IDENTITY_FILTERS = [
  'awaiting',
  'submitted',
  'resubmitted',
  'approved',
  'rejected',
  'pending',
];

export function UserListPage() {
  const ability = useAbility();
  const canManage = ability.can('manage', 'User');
  const [role, setRole] = useState('');
  // 334 (FR-004): every role the platform names, custom roles included, so staff can find everyone holding one.
  const { all: roleNames } = useRoleOptions();
  // 181 (FR-016): the review queue, findable without opening records one at a time.
  // 355 (FR-011): the dashboard's "Identity checks waiting" opens this list on `?identity_status=awaiting`.
  const [searchParams] = useSearchParams();
  const [identityStatus, setIdentityStatus] = useState(() => {
    const fromUrl = searchParams.get('identity_status') ?? '';
    return IDENTITY_FILTERS.includes(fromUrl) ? fromUrl : '';
  });
  // 326 (#684): the driving licence's expiry — valid, expiring within 30 days, expired, none.
  const [licence, setLicence] = useState('');
  // 330 (FR-011): one campus, or `none` for customers who have not chosen yet.
  const [campus, setCampus] = useState('');
  // 376 (#1262, design F): customers or not, whatever their staff role.
  const [customer, setCustomer] = useState('');
  /**
   * 162 (T032): the shared controls, replacing a debounce that parked its timer on
   * **`window._emailTimer`** — a GLOBAL name. A second screen adopting that pattern would have
   * shared one timer with this one, and `CarListPage` already avoided it with a local effect and a
   * comment saying why. `resetOn` carries the role filter, so it obeys the same
   * back-to-page-1 rule as the search (FR-007).
   */
  const {
    page,
    setPage,
    searchInput: whoSearch,
    setSearchInput: setWhoSearch,
    search: debouncedWho,
  } = useListControls({ resetOn: [role, identityStatus, licence, campus, customer] });

  const params = new URLSearchParams({ page: String(page), limit: '20' });
  // 326 (#684): one box for a user's name, email or phone (`q`); `email` stays for other screens.
  if (debouncedWho) params.set('q', debouncedWho);
  if (role) params.set('role', role);
  // 181: absent unless chosen, so the default list is byte-for-byte the request it was before
  // this feature (FR-018).
  if (identityStatus) params.set('identity_status', identityStatus);
  if (licence) params.set('licence', licence);
  if (campus) params.set('campus', campus);
  if (customer) params.set('customer', customer);

  const { data, isLoading, error } = useQuery<UsersResponse>({
    queryKey: [
      ...RESOURCE_KEYS.user.list,
      page,
      debouncedWho,
      role,
      identityStatus,
      licence,
      campus,
      customer,
    ],
    queryFn: () => api.get(`/users?${params.toString()}`),
    // 063 (FR-010): a refusal is not a transient fault. Without this the caller
    // reads the empty state through three retries before the truth arrives.
    retry: false,
  });

  // 330: declared after the list query, so the list is always the page's first request.
  const { data: campusChoices } = useQuery({
    queryKey: ['locations', 'campus-choices'],
    // Public and small; the page itself is staff-only.
    queryFn: () => api.get<{ items: Array<{ _id: string; name: string }> }>('/locations?limit=100'),
  });
  const items = data?.items ?? [];
  const totalPages = data?.pages ?? 1;

  // 037: per-page multi-select. Selection never survives a page or filter
  // change — the ids on screen are the only ids that can be selected.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // biome-ignore lint/correctness/useExhaustiveDependencies: page/filter changes intentionally reset the selection
  useEffect(() => {
    setSelectedIds(new Set());
  }, [page, debouncedWho, role, identityStatus, licence, campus, customer]);

  function toggleRow(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAllPage() {
    setSelectedIds((prev) => {
      const allSelected = items.length > 0 && items.every((u) => prev.has(u._id));
      return allSelected ? new Set<string>() : new Set(items.map((u) => u._id));
    });
  }

  return (
    <div>
      <PageHeader
        title="Users"
        actions={
          canManage ? (
            <Link to="/users/new">
              <Button size="sm">Create user</Button>
            </Link>
          ) : undefined
        }
      />
      <Card className="mb-4 flex flex-wrap gap-3">
        <Input
          type="text"
          placeholder="Search by name, email or phone…"
          aria-label="Search by name, email or phone"
          className="w-full sm:w-72"
          value={whoSearch}
          onChange={(e) => setWhoSearch(e.target.value)}
        />

        <Select
          aria-label="Role"
          value={role}
          onChange={(e) => {
            // The page reset is `useListControls`'s job now, via `resetOn` above.
            setRole(e.target.value);
          }}
        >
          <option value="">All roles</option>
          {roleNames.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </Select>

        <Select
          aria-label="Customer"
          value={customer}
          onChange={(e) => setCustomer(e.target.value)}
        >
          <option value="">Customer: any</option>
          <option value="yes">Customers</option>
          <option value="no">Not customers</option>
        </Select>

        {/* 181 (FR-016): before this, staff found a submission only by opening user records one
            at a time — so an email that was missed, filtered or deleted left no route in at all. */}
        <Select
          aria-label="Identity"
          data-testid="identity-status-filter"
          value={identityStatus}
          onChange={(e) => setIdentityStatus(e.target.value)}
        >
          <option value="">Any identity status</option>
          <option value="awaiting">Awaiting any review</option>
          <option value="submitted">Awaiting review</option>
          <option value="resubmitted">Re-review needed</option>
          <option value="approved">Verified</option>
          <option value="rejected">Rejected</option>
          <option value="pending">Not submitted</option>
        </Select>

        {/* 326 (#684): find customers whose licence has lapsed or is about to, before a booking is
            refused for it. */}
        <Select aria-label="Licence" value={licence} onChange={(e) => setLicence(e.target.value)}>
          <option value="">Any licence</option>
          <option value="valid">Licence valid</option>
          <option value="expiring">Expires within 30 days</option>
          <option value="expired">Licence expired</option>
          <option value="none">No licence expiry</option>
        </Select>

        {/* 330 (FR-011): find a campus's customers, or those who have not chosen yet (for
            example, everyone on a campus that was closed, to reassign them). */}
        <Select aria-label="Campus" value={campus} onChange={(e) => setCampus(e.target.value)}>
          <option value="">Any campus</option>
          {(campusChoices?.items ?? []).map((l) => (
            <option key={l._id} value={l._id}>
              {l.name}
            </option>
          ))}
          <option value="none">Not chosen</option>
        </Select>
      </Card>
      <BulkActionBar
        selectedIds={selectedIds}
        users={items}
        onClear={() => setSelectedIds(new Set())}
      />
      <DataTable
        columns={columns}
        data={items}
        loading={isLoading}
        keyExtractor={(r) => r._id}
        selectable={canManage}
        selectedIds={selectedIds}
        onToggleRow={toggleRow}
        onToggleAllPage={toggleAllPage}
        // 063: administrator-only endpoint. A refused roster is not an empty one.
        error={error as { status?: number } | null}
        pagination={{
          page: page,
          totalPages: totalPages,
          onPageChange: setPage,
        }}
      />{' '}
    </div>
  );
}
